import React, { useEffect, useRef, useState } from "react";
import {
	GAME_FORMATS,
	MAIN_GAME_TIMER,
	PRIMARY_COLOR,
	SECONDARY_COLOR,
} from "../../../Constants/Commons";
import * as FB from "../../Firebase/FirebaseFunctions";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
	BarChart,
	Bar,
	Cell,
	XAxis,
	YAxis,
	LabelList,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from "recharts";
import InputBase from "@mui/material/InputBase";
import { styled } from "@mui/material/styles";
import CountUp from "react-countup";
import _ from "lodash";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { CarouselComponent } from "../../Utils/Carousel";
import { MEASURE } from "../../../instrumentation";
import { INSTRUMENTATION_TYPES } from "../../../instrumentation/types";
import { pauseAudioClip, playAudioClip } from "../../Utils/AudioPlayer";
import { ReactComponent as PlayButtonSvg } from "../../../assets/icons/play-button.svg";
import GeoLocator from "../../Games/SpellBee/GameComponents/GeoLocator/GeoLocator";
import CategoriesRoundContainer from "../../Games/SpellBee/GameComponents/Categories";
import { MatchingColumnsRoundContainer } from "../../Games/SpellBee/GameComponents/matchingColumns";
import { renderRightWrongLetterIcon } from "../../utils";
import { collection, getDocs, addDoc, query, orderBy, limit, where } from "firebase/firestore";
import { db } from "../../../firebase-config";
import { useAuth } from "../../../providers/auth-provider";
import AudioClipContainer from "../../Games/SpellBee/GameComponents/AudioClipContainer";
import { Dialog } from "@mui/material";
import AppButton from "../../Common/AppButton";

const BootstrapInput = styled(InputBase)(({ theme }) => ({
	"label + &": {
		marginTop: theme.spacing(3),
	},
	"& .MuiInputBase-input": {
		borderRadius: 4,
		position: "relative",
		backgroundColor: "rgba(0,0,0,0)",
		color: PRIMARY_COLOR,
		border: "1px solid #ced4da",
		fontSize: 12,
		padding: "6px 26px 6px 12px",
		transition: theme.transitions.create(["border-color", "box-shadow"]),
		"&:focus": {
			borderRadius: 4,
			borderColor: "#80bdff",
			boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
		},
	},
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: PRIMARY_COLOR,
		color: SECONDARY_COLOR,
		fontWeight: "bolder",
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 14,
		color: "white",
		border: "none",
	},
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(odd)": {
		background:
			"linear-gradient(89.91deg, #3A3A3A 80%, rgba(24, 24, 24, 0) 568.67%)",
	},
	"&:nth-of-type(even)": {
		background:
			"linear-gradient(89.91deg, #3A3A3A 80%, rgba(24, 24, 24, 0) 568.67%)",
	},
	// hide last border
	"&:last-child td, &:last-child th": {
		border: 0,
	},
}));

export const UserStats = (props) => {
	const {
		tournamentId,
		poolTournamentId,
		userPoolData,
		isEliminationFinal,
		activeRound,
		isDemoGame,
		data,
		postTournamentPractice = false,
		currentRound,
	} = props;

	const [wordData, setWordsData] = useState([]);
	const [wordList, setWordList] = useState({});
	const [chartData, setChartData] = useState([]);
	const [score, setScore] = useState(0);
	const [pace, setPace] = useState(0);
	const [accuracy, setAccuracy] = useState(0);
	const [correctAttempts, setCorrectAttempts] = useState(0);
	const [noOfAttempts, setNoOfAttempts] = useState(0);
	const [roundFormat, setRoundFormat] = useState(GAME_FORMATS.QUIZ);
	const [aggregateOption, setAggregateOption] = useState(0);
	const [wordRoundOption, setWordRoundOption] = useState("1");
	const [showPassagePopup, setShowPassagePopup] = useState(false);
	const [questionData, setQuestionData] = useState(null);
	const audioElRef = useRef(null);
	const { user } = useAuth();
	const [practiceGameLatestAttempt, setPracticeGameLatestAttempt] = useState(null);

	const _setOverviewData = (round = 0) => {
		let userScore = 0,
			noOfAttempts = 1,
			userPace = 0,
			correctAttempts = 0;
		if (round === 0 && !isEliminationFinal && !practiceGameLatestAttempt) {
			userScore = data.score.reduce((prev, curr) => curr + prev, 0);
			noOfAttempts = data.attempts.reduce((prev, curr) => curr + prev, 0);
			correctAttempts = data.correctAttempts.reduce(
				(prev, curr) => curr + prev,
				0
			);
			userPace = noOfAttempts
				? Math.floor((MAIN_GAME_TIMER * data.daysPlayed.length) / noOfAttempts)
				: 0;
		} else if (!practiceGameLatestAttempt) {
			let leaderboardData = data;
			const dataIndex = leaderboardData.round?.indexOf(String(round)) ?? 0;
			userScore = leaderboardData.score[dataIndex];
			noOfAttempts = leaderboardData.attempts[dataIndex];
			correctAttempts = leaderboardData.correctAttempts[dataIndex];
			userPace = noOfAttempts ? Math.floor(MAIN_GAME_TIMER / noOfAttempts) : 0;
		}

		let userAccuracy = Math.floor((correctAttempts * 100) / noOfAttempts);

		if (postTournamentPractice && practiceGameLatestAttempt) {
			userScore = practiceGameLatestAttempt.score;
			userAccuracy = (practiceGameLatestAttempt.correctAttempts ?? 0) * 100 / (practiceGameLatestAttempt.attempts ?? 1);
			noOfAttempts = practiceGameLatestAttempt.attempts;
			correctAttempts = practiceGameLatestAttempt.correctAttempts;
		}
		setScore(userScore);
		setAccuracy(userAccuracy);
		setPace(userPace);
		setCorrectAttempts(correctAttempts);
		setNoOfAttempts(noOfAttempts);
	};

	const _getWordListDetails = async () => {
		if (postTournamentPractice) {
			return;
		}
		const childId = localStorage.getItem("userId");
		const gameId = localStorage.getItem("gId");
		let data = null;
		if (isDemoGame && gameId) {
			data = await FB.getData("practiceGames", gameId);
			data = [data];
		} else {
			if (isEliminationFinal && poolTournamentId) {
				data = await FB.getAllDocsWithQuery(`children/${childId}/games`, {
					field: "tournamentId",
					operator: "==",
					value: poolTournamentId,
				});
			}
			const tempData = await FB.getAllDocsWithQuery(
				`children/${childId}/games`,
				{
					field: "tournamentId",
					operator: "==",
					value: tournamentId,
				}
			);

			if (data) {
				data = [...data, ...tempData];
			} else {
				data = tempData;
			}
		}
		const newData = _.orderBy(data, ["round"], ["asc"]);
		setRoundFormat(newData[0].format);
		setWordsData(newData);
		// FB.getAllDocsWithQuery(`children/${childId}/games`, {
		//   field: "tournamentId",
		//   operator: "==",
		//   value: tournamentId,
		// })
		//   .then((data) => {
		//     const newData = _.orderBy(data, ["round"], ["asc"]);
		//     setRoundFormat(newData[0].format);
		//     setWordsData(newData);
		//   })
		//   .catch((e) => {
		//     console.log("Error!", e);
		//   });
	};

	// useEffect(() => {
	//   const audioEl = document.querySelector("audio");
	//   audioElRef.current = audioEl;
	// }, []);

	const handlePlay = () => {
		const audioEl = document.querySelector("audio");
		audioEl.play();
	};

	const _setWordList = (round) => {
		const wordDataByRound = wordData.find((wd) => wd.round === round);
		setRoundFormat(wordDataByRound.format);
		setWordList(wordDataByRound);
	};

	const handleChange = (event) => {
		setAggregateOption(event.target.value);
		_setOverviewData(event.target.value);
	};

	const handlePracticeChange = (round) => {
		setAggregateOption(round);
		_setOverviewData(round);
	};


	const handleWordRoundChange = (event) => {
		setWordRoundOption(event.target.value);
		_setWordList(event.target.value);
	};

	// const pauseAnyPlayingAudio = () => {
	//   if (audioElRef && !audioElRef.paused) {
	//     pauseAudioClip();
	//   }
	// };

	useEffect(() => {

		if (postTournamentPractice) {
			const queryRef = query(
				collection(db, 'children', user?.id, 'completedPracticeGames'),
				where('tournamentId', '==', tournamentId),
				orderBy('endTime', 'desc'),
				limit(1)
			);

			getDocs(queryRef).then((querySnapshot) => {
				if (querySnapshot.empty) {
					console.log("No practice game found");
					return;

				}
				const game = querySnapshot.docs.map(doc => ({
					...doc.data(), id: doc.id
				}))[0];
				setPracticeGameLatestAttempt(game);
				setRoundFormat(game.format);
				setWordsData([game]);
				setWordList(game);
			})
		}
	}, [postTournamentPractice]);

	useEffect(() => {
		if (postTournamentPractice && currentRound && practiceGameLatestAttempt) {
			setAggregateOption(currentRound);
			_setOverviewData(currentRound);
			// setWordRoundOption(currentRound);
		}
	}, [currentRound, postTournamentPractice, practiceGameLatestAttempt]);
	useEffect(() => {
		MEASURE(
			INSTRUMENTATION_TYPES.USER_STATS_VIEWED,
			localStorage.getItem("userId"),
			{}
		);
		if (data && data.id) {
			let dataIndex = Number(data.round[data.round.length - 1]);
			if (isEliminationFinal) {
				dataIndex = dataIndex + 1;
			}
			setAggregateOption(dataIndex);
			_setOverviewData(dataIndex);
			_getWordListDetails();
			const cData = [];
			// set chart data
			for (let i = 0; i < Number(activeRound); i++) {
				// const filteredData = data.find((d) => d.round == i + 1);
				const isDataPresent = data.round.includes(String(i + 1));
				cData.push({
					name: `R ${isDataPresent ? data.round[i] : i + 1}`,
					score: isDataPresent ? data.score[i] : 0,
					attempts: isDataPresent ? data.attempts[i] - data.score[i] : 0,
				});
			}
			setChartData(cData);
		}
	}, [data]);

	useEffect(() => {
		if (wordData && wordData.length) {
			setWordRoundOption(wordData[wordData.length - 1].round);
			_setWordList(wordData[wordData.length - 1].round);
		}
	}, [wordData]);

	const renderCards = (roundFormat) => {
		if (!wordList.attemptedWords.length) {
			return <></>;
		}
		return wordList.attemptedWords.map((attr, idx) => {
			const temp = `${attr}`;
			const finalString = temp
				.replace(/"/g, '"')
				.replace(/'/g, "'")
				.replace(/style=\"(.*?)\"/g, (match, p1) => {
					const replaced = p1.replace(/"/g, '\\\\"');
					return `style=\\"${replaced}\\"`;
				});
			const attrObj = JSON.parse(finalString);
			return (
				<div
					key={`com-${idx}`}
					className="w-full text-sm p-3 overflow-hidden bg-[rgba(58,58,58,0.9)]">
					{roundFormat === GAME_FORMATS.AUDIOCLIP ? (
						<div className="flex gap-4 items-center px-2 py-4">
							<audio
								className="absolute -top-[9999px]"
								//src={attrObj.audioClip}
								//preload="auto"
								ref={audioElRef}
							/>
							<div
								className="iconButtonContainer p-4 !w-[78px] !h-[78px]"
								onClick={(e) => playAudioClip(attrObj.audioClip)}>
								<PlayButtonSvg className="h-[50px] w-[50px]" />
							</div>
							<div className="text-sm mt-2 text-center text-white font-bold">
								PLAY AUDIO
							</div>
						</div>
					) : (
						<></>
					)}
					{[
						GAME_FORMATS.IMAGE,
						GAME_FORMATS.IMAGE_JUMBLED,
						GAME_FORMATS.FLASH_IMAGES,
					].includes(roundFormat) ? (
						<div
							style={{
								backgroundImage: `url(${attrObj.imageUrl})`,
								height: "30vh",
								backgroundPosition: "center",
								backgroundSize: "contain",
								backgroundRepeat: "no-repeat",
							}}
						/>
					) : null}
					<div
						style={{
							margin: "10px",
							color: "#ccf900",
						}}>
						Attempt Number: <span style={{ color: "white" }}>{idx + 1}</span>
						<br />
						<br />
						{roundFormat === GAME_FORMATS.IMAGE_JUMBLED ? (
							<>
								Jumbled Word:{" "}
								<span style={{ color: "white", whiteSpace: "pre-line" }}>
									{wordList?.jumbledString[idx]}
								</span>
							</>
						) : (
							<>
								Question:{" "}
								<span
									style={{ color: "white", whiteSpace: "pre-line" }}
									dangerouslySetInnerHTML={{
										__html: attrObj.question,
									}}></span>
							</>
						)}
						<br />
						<br />
						Your Response:{" "}
						<span style={{ color: "white" }}>{wordList.responses[idx]}</span>
						<br />
						<br />
						Answer: <span style={{ color: "white" }}>{attrObj.answer}</span>
						<br />
						<br />
						{wordList.results ? (
							<>
								Result:{" "}
								<span style={{ color: "white" }}>
									{wordList.responses[idx]
										? wordList.results[idx]
											? "Correct"
											: "Incorrect"
										: "Skipped"}
								</span>
								<br />
								<br />
							</>
						) : null}
						{attrObj.solution && attrObj.solution !== "null" ? (
							<>
								Solution:{" "}
								<span style={{ color: "white" }}>{attrObj.solution}</span>
								<br />
								<br />{" "}
							</>
						) : null}
					</div>
				</div>
			);
		});
	};

	const renderThisOrThatCards = () => {
		if (!wordList.attemptedWords.length) {
			console.log("No attempted words found for this or that");
			return <></>;
		}
		return wordList.attemptedWords.map((attr, idx) => {
			const temp = `${attr}`;
			const finalString = temp
				.replace(/"/g, '"')
				.replace(/'/g, "'")
				.replace(/style=\"(.*?)\"/g, (match, p1) => {
					const replaced = p1.replace(/"/g, '\\\\"');
					return `style=\\"${replaced}\\"`;
				});
			const attrObj = JSON.parse(finalString);
			return (
				<div
					key={`com-${idx}`}
					className="w-full text-sm p-3 overflow-hidden bg-[rgba(58,58,58,0.9)]">
					<div
						style={{
							margin: "10px",
							color: "#ccf900",
						}}>
						Attempt Number: <span style={{ color: "white" }}>{idx + 1}</span>
						<br />
						<br />

						<>
							Question:{" "}
							<span
								style={{ color: "white", whiteSpace: "pre-line" }}
								dangerouslySetInnerHTML={{
									__html: attrObj.question,
								}}></span>
						</>
						<br />
						<br />
						Your Response:{" "}
						<ResponseItem response={wordList.responses[idx]} />
						<br />
						
						<br />
						{wordList.results ? (
							<>
								Result:{" "}
								<span style={{ color: "white" }}>
									{wordList.responses[idx]
										? wordList.results[idx]
											? "Correct"
											: "Incorrect"
										: "Skipped"}
								</span>
								<br />
								<br />
							</>
						) : null}
						{/* {!wordList.results[idx] ? ( */}
							<>
								Answer:{" "}
								<ResponseItem response={attrObj.answer} />
								<br />
								<br />
							</>
						{/* ) : null} */}
						{attrObj.solution && attrObj.solution !== "null" ? (
							<>
								Solution:{" "}
								<span style={{ color: "white" }}>{attrObj.solution}</span>
								<br />
								<br />{" "}
							</>
						) : null}
					</div>
				</div>
			);
		});
	};

	function ResponseItem({ response }) {
		// A quick regex to see if the URL ends with a common image extension (possibly followed by query params)
		const isImageUrl = (url) =>
			typeof url === "string" &&
			/\.(png|jpe?g|gif|bmp|webp)(\?.*)?$/i.test(url);
		if (isImageUrl(response)) {
			return (
				<img
					src={response}
					alt="response-image"
					style={{ maxWidth: "100%", display: "block" }}
				/>
			);
		} else {
			return <span style={{ color: "white" }}>{response}</span>;
		}
	}

	const renderTangramCards = () => {
		if (!wordList.attemptedWords.length) {
			return <></>;
		}
		return wordList.attemptedWords.map((attr, idx) => {
			const temp = `${attr}`;
			const finalString = temp
				.replace(/"/g, '"')
				.replace(/'/g, "'")
				.replace(/style=\"(.*?)\"/g, (match, p1) => {
					const replaced = p1.replace(/"/g, '\\\\"');
					return `style=\\"${replaced}\\"`;
				});
			const attrObj = JSON.parse(finalString);

			return (
				<div className="w-full text-sm p-3 overflow-hidden bg-[rgba(58,58,58,0.9)] flex flex-col gap-3">
					<div
						key={`tangram-${idx}`}
						style={{
							backgroundImage: `url(${attrObj.imageUrl})`,
							height: "30vh",
							backgroundPosition: "center",
							backgroundSize: "contain",
							backgroundRepeat: "no-repeat",
						}}
					/>
					<div
						style={{
							margin: "10px",
							color: "#ccf900",
						}}>
						Result:{" "}
						<span className="text-white">
							{wordList.results?.[idx]
								? `You solved it in ${Math.floor(
									wordList.scoreBreakdown?.[idx]?.timeTaken
								)} secs`
								: "You couldn't solve it"}
						</span>
					</div>
					<br />
				</div>
			);
		});
	};

	const getMatchingIndexes = (scoreBreakdown) => {
		const correctPairs = scoreBreakdown.correctPairs;
		const matchingIndexes = [];

		correctPairs.forEach((pair, index) => {
			if (pair.itemOne.matchId === pair.itemTwo.matchId) {
				matchingIndexes.push(index);
			}
		});

		return matchingIndexes;
	};

	const getLettersGiven = (letters) => {
		const lettersString = letters.map(item => item.letter).join(', ')
		return lettersString;
	}

	const renderScrabbleCards = () => {
		if (!wordList.responseWords.length) {
			return <></>;
		}
		return wordList.responseWords.map((attr, idx) => {

			return (
				<div className="w-full text-sm p-3 overflow-hidden bg-[rgba(58,58,58,0.9)] flex flex-col gap-3 text-white">
					<div className="flex gap-3 ">
						<span>Question:</span>
						<div className="">
							{`Make a ${attr.wordSize} letter word`}
						</div>
					</div>
					<div className="flex gap-3 ">
						<span>Letter given:</span>
						<div className="">
							{getLettersGiven(attr.letters)}
						</div>
					</div>
					<div className="flex gap-3 ">
						<span>Word possible:</span>
						<div className="">
							{attr.possibleWord}
						</div>
					</div>
					<div className="flex gap-3 ">
						<span>You made:</span>
						<div className="">
							{attr.answer}
						</div>
					</div>
					<div className="flex gap-3 ">
						<span>Score:</span>
						<div className="">
							{attr.score}
						</div>
					</div>
				</div>
			)
		})
	}

	const returnDiscountCode = (option) => {
		switch (option.discountMode) {
			case "percentage":
				return `- ${option.discount}%`;
			case "mrpDiscount":
				return `- ₹${option.discount}`;
			case "x+y":
				return `${option.discount.buy} + ${option.discount.free} FREE`;
			default:
				return ""
		}
		return "";
	};

	const ShoppingItemOptions = (currentItem, index) => {
		console.log("currentItem", currentItem);

		const showDiscountColumn = currentItem.givenOptions.some((option) => {
			const discount = returnDiscountCode(option);
			return discount && discount.trim() !== "";
		});

		const showDeliveryColumn = currentItem.givenOptions.some(
			(option) => option?.delivery
		);

		// Calculate the number of columns to display in the grid.
		const totalColumns =
			4 - (!showDiscountColumn ? 1 : 0) - (!showDeliveryColumn ? 1 : 0);
		const gridColsClass = `grid-cols-${totalColumns}`;

		// If an option exists in both arrays, it is sorted to the same position as in selectedOptions.
		const sortCorrectOptions = (correctOptions) => {

			const matchingOptions = {};
			const nonMatchingOptions = [];

			correctOptions.forEach((item) => {
				if (
					currentItem.selectedOptions.some(
						(givenItem) => givenItem.option === item.option
					)
				) {
					matchingOptions[item.option] = item;
				} else {
					nonMatchingOptions.push(item);
				}
			});

			const sorted = [];

			currentItem.selectedOptions.forEach((givenItem) => {
				if (matchingOptions.hasOwnProperty(givenItem.option)) {
					sorted.push(matchingOptions[givenItem.option]);
					delete matchingOptions[givenItem.option];
				} else if (nonMatchingOptions.length > 0) {
					sorted.push(nonMatchingOptions.shift());
				}
			});

			return sorted;
		};

		const sortedCorrectOptions = sortCorrectOptions(currentItem.correctOptions);

		return (
			<div className="flex flex-col gap-2 bg-[#4a4a4a] text-white rounded-lg p-3 text-[14px] w-[95vw]">
				{console.log("currentItem", currentItem)}
				<span className="text-[18px] text-center mb-4">
					List {currentItem.currentActiveIndex + 1} item {currentItem.subindex + 1} - {currentItem.item} x {currentItem.quantity}
				</span>

				{/* Header Row for givenOptions */}
				<div className={`grid ${gridColsClass} gap-2`}>
					<div className="flex flex-col items-center justify-center">
						<span>App</span>
						<br />
					</div>
					<div className="flex flex-col items-center">
						<span>Price</span>
						<span className='text-[10px]'>(per item)</span>
					</div>
					{/* {showDiscountColumn && <div className='flex flex-col items-center'>Discount</div>} */}
					{showDiscountColumn && <div className="flex flex-col items-center">
						<span>Discount</span>
						<span className='text-[10px]'>(per item)</span>
					</div>}
					{showDeliveryColumn && <div className='flex flex-col items-center'>Delivery</div>}
				</div>

				{/* Option Rows for givenOptions */}
				<div className="flex flex-col gap-2">
					{currentItem.givenOptions.map((option, idx) => (
						<div
							key={idx}
							className={`bg-[#3a3a3a] rounded-lg py-2 items-center grid ${gridColsClass} gap-2`}
						>
							<div className="p-1 items-left justify-left">{option.option}</div>
							<div className='flex flex-col items-center justify-center'>₹{option.price}</div>
							{/* {showDiscountColumn && <div className='flex flex-col items-center justify-center text-center text-[12px]'>{returnDiscountCode(option) === "" ? "-" : returnDiscountCode(option)}</div>} */}
							{showDiscountColumn && (
								<div
									className={`flex flex-col items-center justify-center text-center ${returnDiscountCode(option).includes("FREE") ? "text-[12px]" : "text-[14px]"
										}`}
								>
									{returnDiscountCode(option) === "" ? "-" : returnDiscountCode(option)}
								</div>
							)}
							{showDeliveryColumn && <div className="flex items-center justify-center">{option.delivery ? `+ ₹${option.delivery}` : "FREE"}</div>}
						</div>
					))}
				</div>

				{/* Section for Selected and Correct Options */}
				<div className="flex  flex-col gap-2 items-start justify-center mt-5 mb-2">
					{/* Selected Options */}
					<div className="flex gap-2">
						<span>Your Pick : ₹{currentItem.finalCost}, </span>
						{currentItem.selectedOptions.map((option, idx) => (
							<div key={idx} className="rounded-lg flex items-center gap-2">
								<div>{option.option}</div>
								<div>x {option.selected}</div>
							</div>
						))}
					</div>

					{/* Separator */}
					{/* <div className="flex flex-col items-start justify-start"> | </div> */}

					{/* Correct Options (sorted to align with selectedOptions order when applicable) */}
					<div className="flex gap-2">
						<span>Cheapest : ₹{currentItem.cheapestTotal}, </span>
						{sortedCorrectOptions.map((option, idx) => (
							<div key={idx} className="rounded-lg flex items-center justify-start gap-2">
								<div>{option.option}</div>
								<div>x {option.quantity}</div>
							</div>
						))}
					</div>
				</div>
			</div>
		);
	};


	const renderShoppingCartCards = () => {
		if (!wordList.responsePurchases.length) {
			return <></>;
		}

		const counters = {};
		const withSubindex = wordList.responsePurchases.map(item => {
			const key = item.currentActiveIndex;
			if (counters[key] === undefined) counters[key] = 0;
			const subindex = counters[key]++;
			return { ...item, subindex };
		});
		return withSubindex.map((attr, idx) => {
			return ShoppingItemOptions(attr, idx);
		});
	};

	const renderParagraphPopup = () => {
		return (
			<Dialog
				open={showPassagePopup}
				onClose={() => setShowPassagePopup(false)}
				className="register-success w-[100vw]"
			>

				<div className="relative flex flex-col bg-white text-black h-full overflow-hidden items-center justify-center py-[20px]">
					<ReadingComprehensionContainer question={questionData} isPopup={true} />
				</div>


			</Dialog>
		);
	}

	function ReadingComprehensionContainer({ question, isPopup = false }) {
		return (
			<div
				style={{
					// height: "50vh",
					height: isPopup ? "60vh" : "50vh",
					overflowY: "scroll",
					overflowX: "hidden",
					border: "2px solid black",
					padding: "1.5rem",
					borderRadius: "12px",
					boxSizing: "border-box",
					width: "90%",
					margin: "0 auto",

				}}
				dangerouslySetInnerHTML={{
					__html: question?.content,
				}}
			>
				
			</div>

		);
	}

	const renderReadingComprehensionCards = () => {
		if (!wordList.userResponses.length) {
			return <></>;
		}

		let subindex = 0;
		let mainIndex = 0;
		const userREsponsesWithSubindex = wordList.userResponses.map((attr, idx) => {
			if (attr.currentActiveIndex !== mainIndex) {
				subindex = 0;
				mainIndex = attr.currentActiveIndex;
			}

			return {
				...attr,
				subindex: subindex++
			}
		});


		return userREsponsesWithSubindex.map((attr, idx) => {
			return (
				<div
					key={`audio-${idx}`}
					className="w-full text-sm p-3 overflow-hidden bg-[rgba(58,58,58,0.9)] flex flex-col gap-3 text-white p-2"
				>
					<div className="flex flex-col items-center justify-center w-full bg-white text-black p-4 text-base rounded-md">

						{/* Centered Header */}
						<div className="text-lg font-bold text-center mb-2">
							Passage {attr.currentActiveIndex + 1} - Question {attr.subindex + 1}
						</div>

						{/* Centered Button */}
						<AppButton
							onClick={() => {
								setQuestionData(attr);
								setShowPassagePopup(true);
							}}
							className="rounded-[115px] min-w-[159px] w-[159px] h-[35px] min-h-[35px] mb-4"
						>
							View Passage
						</AppButton>

						{/* Content with spacing */}
						<div className="flex flex-col space-y-2 text-sm items-start w-full">
							<span><strong>Question:</strong> {attr.question}</span>
							<span><strong>Answer:</strong> {attr.answer}</span>
							<span><strong>Your Response:</strong> {attr.selectedOption}</span>
							<span>
								<strong>Result:</strong>{" "}
								{attr.answer === attr.selectedOption ? "Correct" : "Incorrect"}
							</span>
						</div>
					</div>
				</div>
			);
		});
	};


	const renderAudioComprehensionCards = () => {
		if (!wordList.userResponses.length) {
			return <></>;
		}
		let subindex = 0;
		let mainIndex = 0;
		const userREsponsesWithSubindex = wordList.userResponses.map((attr, idx) => {
			if (attr.currentActiveIndex !== mainIndex) {
				subindex = 0;
				mainIndex = attr.currentActiveIndex;
			}

			return {
				...attr,
				subindex: subindex++
			}
		});


		return userREsponsesWithSubindex.map((attr, idx) => {
			return (
				<div
					key={`audio-${idx}`}
					className="w-full text-sm p-3 overflow-hidden bg-[rgba(58,58,58,0.9)] flex flex-col gap-3 text-white p-2"
				>
					<div className="flex flex-col items-center justify-center w-full bg-white text-black p-4 text-base rounded-md">

						<div className="text-lg font-bold text-center mb-2">
							Audio {attr.currentActiveIndex + 1} - Question {attr.subindex + 1}
						</div>

						<div className="flex flex-col items-center px-2 py-4">

							<div
								className="iconButtonContainer p-4 !w-[60px] !h-[60px]"
								onClick={(e) => playAudioClip(attr.audioClip)}>
								<PlayButtonSvg className="h-[40px] w-[40px]" />
							</div>
							<div className="text-sm mt-2 text-center text-black font-bold">
								PLAY
							</div>
						</div>

						{/* Content with spacing */}
						<div className="flex flex-col space-y-2 text-sm items-start w-full">
							<span><strong>Question:</strong> {attr.question}</span>
							<span><strong>Answer:</strong> {attr.answer}</span>
							<span><strong>Your Response:</strong> {attr.selectedOption}</span>
							<span>
								<strong>Result:</strong>{" "}
								{attr.answer === attr.selectedOption ? "Correct" : "Incorrect"}
							</span>
						</div>
					</div>
				</div>
			);
		});
	};











	const renderRacoonDienesCards = () => {
		if (!wordList.scoreBreakdown.length) {
			return <></>;
		}

		return wordList.scoreBreakdown.map((attr, idx) => {
			const entries = Object.entries(attr.selectedBlocks)
				.filter(([k]) => !isNaN(Number(k)));
			const blocksMap = new Map(entries);

			const summary = Array.from(blocksMap.entries())
				.sort(([a], [b]) => b - a)
				.map(([block, count]) => `${block} x ${count}`)
				.join(', ');

			return (
				<div className="w-full text-sm p-3 overflow-hidden bg-[rgba(58,58,58,0.9)] flex flex-col gap-3 text-white">
					<div className="flex gap-3 ">
						<span>Target: {attr.targetNumber} </span>
					</div>
					<div className="flex gap-3 ">
						<span>You made: {attr.numberMade}
						</span>
					</div>
					<div className="flex gap-3 ">
						<span>collected: {summary}</span>
					</div>

					<div className="flex gap-3 ">
						<span>Result: {attr.numberMade === attr.targetNumber ? "Correct" : "Incorrect"}</span>
					</div>
				</div>
			);
		});

	};


	const renderHangManCards = () => {
		if (!wordList.attemptedWords.length) {
			return <></>;
		}
		return wordList.attemptedWords.map((attr, idx) => {
			const temp = `${attr}`;
			const finalString = temp
				.replace(/"/g, '"')
				.replace(/'/g, "'")
				.replace(/style=\"(.*?)\"/g, (match, p1) => {
					const replaced = p1.replace(/"/g, '\\\\"');
					return `style=\\"${replaced}\\"`;
				});
			const attrObj = JSON.parse(finalString);

			const index = wordList.wordResponses.findIndex(
				(wordResponse) => wordResponse.word === attrObj.word
			);

			const response = wordList.wordResponses[index];
			return (
				<div className="w-full text-sm p-2 overflow-hidden bg-[rgba(58,58,58,0.9)] flex flex-col gap-4 text-white my-4">

					<div className="flex items-end">
						Guesses:
						{response?.guessedLetters?.map((letter) => {
							return renderRightWrongLetterIcon(letter, response.word)
						})}
					</div>

					<div className="flex items-center">
						Word:
						<div className="w-full px-2">
							{response?.word?.split("").map((letter, index) => (
								<span key={index} className="inline-block mx-1 font-bold uppercase">
									{letter.toLowerCase()}
								</span>
							))}
						</div>
					</div>

					<div className="flex items-center">
						Hint:
						<div className="w-full px-2">
							{attrObj.hint}
						</div>
					</div>

					<div className="flex items-center">
						Result:
						<div className="w-full px-2">
							{response?.word &&
								(response.word
									.split("")
									.every(letter =>
										response.guessedLetters.includes(letter.toLowerCase())
									) ? (
									<span className="inline-block mx-1">
										You Guessed It
									</span>
								) : (
									<span className="inline-block mx-1">
										You Couldn't Guess It
									</span>
								))}
						</div>
					</div>

				</div>

			);
		});
	};

	const rendermMatchingColumnCards = () => {
		if (!wordList.attemptedWords.length) {
			return <></>;
		}
		return wordList.attemptedWords.map((attr, idx) => {
			const temp = `${attr}`;
			const finalString = temp
				.replace(/"/g, '"')
				.replace(/'/g, "'")
				.replace(/style=\"(.*?)\"/g, (match, p1) => {
					const replaced = p1.replace(/"/g, '\\\\"');
					return `style=\\"${replaced}\\"`;
				});
			const attrObj = JSON.parse(finalString);

			const totalCategories = attrObj?.length


			const correctResponses = getMatchingIndexes(wordList?.scoreBreakdown?.[idx])
			const totlaFound = correctResponses?.length
			return (
				<div className="w-full text-sm p-3 overflow-hidden bg-[rgba(58,58,58,0.9)] flex flex-col gap-3">
					<MatchingColumnsRoundContainer
						currentActiveQuestion={{ pairs: attrObj }}
						currentActiveIndex={idx}
						responseView={true}
						responseArray={attrObj}
						correctResponses={correctResponses}
					/>
					<div
						style={{
							margin: "10px",
							color: "#ccf900",
							fontSize: "18px",
						}}>
						You Matched:{" "}
						<span className="text-white">
							{totlaFound}/{totalCategories}
						</span>
					</div>
					<br />
				</div>
			);
		});

	}

	const handleNumber = (num) => {
		if (num < 0) {
			return `<-- ${Math.abs(num)}`;
		} else if (num > 0) {
			return `${num} -->`;
		} else {
			return "0";
		}
	};

	const renderNumberLineCards = () => {
		if (!wordList.scoreBreakdown.length) {
			return <></>;
		}
		return wordList.scoreBreakdown.map((attr, idx) => {
			return (
				<div className="w-full text-sm p-3 overflow-hidden bg-[rgba(58,58,58,0.9)] flex flex-col gap-3 text-white">

					<div className="flex gap-3 ">
						<span>Package Location:</span>
						<div className="">
							{/[+\-×]/.test(attr?.question) ? `${attr?.question} = ${attr?.correctAnswer}` : attr?.question}
						</div>
					</div>

					<div className="flex gap-3 ">
						<span>Your Location:</span>
						<div className="">
							{attr?.previousLocation}
						</div>
					</div>

					<div className="flex gap-3 ">
						<span>Steps needed:</span>
						<div className="">
							{handleNumber(attr?.correctStepsToMove)}
						</div>
					</div>

					<div className="flex gap-3 ">
						<span>Steps Made:</span>
						<div className="">
							{handleNumber(attr?.stepsMoved)}
						</div>
					</div>

					<div className="flex gap-3 ">
						<span>Result:</span>
						<div className="">
							{attr?.result}
						</div>
					</div>


				</div>

			);
		});
	};

	const renderCategryCards = () => {
		if (!wordList.attemptedWords.length) {
			return <></>;
		}
		return wordList.attemptedWords.map((attr, idx) => {
			const temp = `${attr}`;
			const finalString = temp
				.replace(/"/g, '"')
				.replace(/'/g, "'")
				.replace(/style=\"(.*?)\"/g, (match, p1) => {
					const replaced = p1.replace(/"/g, '\\\\"');
					return `style=\\"${replaced}\\"`;
				});
			const attrObj = JSON.parse(finalString);

			const totalCategories =
				attrObj?.length /
				attrObj.filter((item) => item.category === attrObj?.[0]?.category)
					?.length;
			const totlaFound =
				wordList?.scoreBreakdown?.[idx]?.correctCategories?.length ?? 0;
			return (
				<div className="w-full text-sm p-3 overflow-hidden bg-[rgba(58,58,58,0.9)] flex flex-col gap-3">
					<CategoriesRoundContainer
						currentActiveQuestion={{ questions: attrObj }}
						currentActiveIndex={idx}
						responseView={true}
						responseArray={attrObj}
						correctCategoriesAnswered={
							wordList?.scoreBreakdown?.[idx]?.correctCategories
						}
					/>
					<div
						style={{
							margin: "10px",
							color: "#ccf900",
							fontSize: "18px",
						}}>
						You Found:{" "}
						<span className="text-white">
							{totlaFound}/{totalCategories}
						</span>
					</div>
					<br />
				</div>
			);
		});
	};

	const groupResponseWordsByLetters = (responseWords) => {
		const groups = {};

		responseWords.forEach(word => {

			const key = word.letters.map(letterObj => letterObj.letter).join('');

			if (!groups[key]) {
				groups[key] = [];
			}
			groups[key].push(word);
		});

		return Object.values(groups);
	};

	const renderGeoLocatorCards = () => {
		if (!wordList.attemptedWords.length) {
			return <></>;
		}
		return wordList.attemptedWords.map((attr, idx) => {
			const temp = `${attr}`;
			const finalString = temp
				.replace(/"/g, '"')
				.replace(/'/g, "'")
				.replace(/style=\"(.*?)\"/g, (match, p1) => {
					const replaced = p1.replace(/"/g, '\\\\"');
					return `style=\\"${replaced}\\"`;
				});
			const attrObj = JSON.parse(finalString);
			return (
				<div className="w-full text-sm p-3 overflow-hidden bg-[rgba(58,58,58,0.9)] flex flex-col gap-3">
					<GeoLocator
						currentActiveQuestion={{
							...attrObj,
							markerLocation: wordList.scoreBreakdown?.[idx]?.markerLocation,
						}}
						currentActiveIndex={idx}
						responseView={true}
					/>

					<div
						style={{
							margin: "2px",
							color: "#ccf900",
						}}>
						City:{" "}
						<span className="text-white">
							{wordList.scoreBreakdown?.[idx]?.city}
						</span>
					</div>
					{wordList.scoreBreakdown?.[idx]?.distance >= 0 && (
						<div
							style={{
								margin: "2px",
								color: "#ccf900",
							}}>
							Distance to your marker:{" "}
							<span className="text-white">
								{wordList.scoreBreakdown?.[idx]?.distance} km
							</span>
						</div>
					)}
					<div
						style={{
							margin: "2px",
							color: "#ccf900",
						}}>
						Score:{" "}
						<span className="text-white">
							{wordList.scoreBreakdown?.[idx]?.currentRoundScore}
						</span>
					</div>
					<br />
				</div>
			);
		});
	};

	return (
		<div className="h-full overflow-auto space-y-4 pb--[20%] md:pb-[10%]">
			<div
				className="mx-2 rounded-xl p-4"
				style={{
					background:
						"linear-gradient(89.91deg, #3A3A3A -7.1%, rgba(24, 24, 24, 0) 568.67%)",
				}}>
				<div style={{ color: "white", fontSize: 14 }}>
					{!postTournamentPractice && <Select
						id="t-s-select"
						sx={{
							".MuiInputBase-input": {
								fontSize: {
									md: "16px",
								},
							},
							color: "white",
							".MuiOutlinedInput-notchedOutline": {
								borderColor: "rgba(228, 219, 233, 0.25)",
							},
							"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
								borderColor: "rgba(228, 219, 233, 0.25)",
							},
							"&:hover .MuiOutlinedInput-notchedOutline": {
								borderColor: "rgba(228, 219, 233, 0.25)",
							},
							".MuiSvgIcon-root ": {
								fill: "white !important",
							},
						}}
						value={aggregateOption}
						label="Select Round"
						onChange={handleChange}
						input={<BootstrapInput />}>
						{!isEliminationFinal && <MenuItem value={0}>Overall</MenuItem>}
						{Array.from(Array(Number(activeRound))).map((e, i) => (
							<MenuItem key={i} value={i + 1}>
								Round {i + 1}
							</MenuItem>
						))}
					</Select>}
				</div>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						color: "#f5f5f5",
						fontSize: 16,
						textAlign: "center",
					}}>
					<div>
						<span className="text-[11px] md:text-base">SCORE</span> <br />
						<span style={{ color: "#CCF900", fontWeight: 800, fontSize: 65 }}>
							<CountUp end={score} duration={0.75} />
						</span>
					</div>
					<div className="text-[11px] md:text-sm">
						OVERALL
						<br />
						PROGRESS
						<br />
					</div>
					<img
						src="/Assets/Images/trikon.png"
						height="60px"
						width="auto"
						alt="trikon"
					/>
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							marginTop: 0,
						}}>
						{roundFormat === GAME_FORMATS.CODING_ALGOS ||
							roundFormat === GAME_FORMATS.TANGRAM ? (
							<div className="data-round-container">
								<div className="data-round" style={{ flexDirection: "column" }}>
									<CountUp
										end={correctAttempts}
										duration={0.75}
										style={{ flexDirection: "column" }}
									/>
									{/* <span style={{ fontSize: 10 }}>secs/attempt</span> */}
								</div>
								<div className="text-[11px] md:text-base">PROBLEMS SOLVED</div>
							</div>
						) : (
							<div className="data-round-container">
								<div className="data-round" style={{ flexDirection: "column" }}>
									<CountUp
										end={noOfAttempts}
										duration={0.75}
										style={{ flexDirection: "column" }}
									/>
								</div>
								<div style={{ fontSize: 11 }}>QUESTIONS</div>
							</div>
						)}
						<div className="data-round-container">
							<div className="data-round">
								<CountUp end={accuracy} duration={0.75} />%
							</div>
							<div className="text-[11px] md:text-base">ACCURACY</div>
						</div>
					</div>
				</div>
			</div>
			{/*<div
        className="mx-2 rounded-xl p-4"
        style={{
          // width: "96%",
          // minHeight: "233px",
          background:
            "linear-gradient(89.91deg, #3A3A3A -7.1%, rgba(24, 24, 24, 0) 568.67%)",
          borderRadius: "12px",
          // margin: "0 8px",
          padding: "16px",
        }}
      >
        <div
          style={{
            width: "100%",
            textAlign: "center",
            color: "#f5f5f5",
            margin: 10,
          }}
        >
          TRENDLINE
        </div>
        {data && data.id ? (
          <div style={{ width: "100%", height: "220px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={317}
                height={141}
                data={chartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 20,
                }}
              >
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  tick={{ fill: "#f5f5f5" }}
                  style={{
                    fontSize: "12px",
                  }}
                />
                <Tooltip />
                <defs>
                  <linearGradient
                    id="colorUv"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="100%"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0" stopColor="#ccf900" />
                    <stop offset="1" stopColor="#a0c300" />
                  </linearGradient>
                </defs>
                <Legend
                  formatter={(value) => (
                    <span style={{ color: "#f5f5f5", fontSize: "12px" }}>
                      {value}
                    </span>
                  )}
                />
                <Bar dataKey="score" barSize={40} fill="url(#colorUv)">
                  <LabelList
                    dataKey="score"
                    position="top"
                    offset={10}
                    style={{
                      textAnchor: "middle",
                      fontSize: "80%",
                      fill: "#ccf900",
                    }}
                  />
                </Bar>
                {/* <Bar
                  dataKey="attempts"
                  stackId="a"
                  barSize={40}
                  fill="#7f7f7f"
                  style={{ borderRadius: "50px" }}
                >
                  <LabelList
                    dataKey="attempts"
                    position="left"
                    offset={10}
                    style={{
                      textAnchor: "middle",
                      fontSize: "80%",
                      fill: "#f5f5f5",
                    }}
                  />
                </Bar> */}
			{/*</BarChart>
            </ResponsiveContainer>
          </div>
        ) : null}
      </div>*/}
			<div
				className="mx-2 rounded-xl py-4 md:p-4 !mb-[25%] md:!mb-[15%]"
				style={{
					// marginTop: "10px",
					// width: "96%",
					// minHeight: "110px",
					background:
						"linear-gradient(89.91deg, #3A3A3A -7.1%, rgba(24, 24, 24, 0) 568.67%)",
					// borderRadius: "12px",
					// margin: "6px",
					// marginBottom: "100px",
				}}>
				<div style={{ margin: "10px", color: "white", fontSize: 16 }}>
					<div
						style={{ textAlign: "center", marginBottom: 10, color: "#f5f5f5" }}>
						YOUR ATTEMPTS
					</div>
					<div>
						{wordData && wordData.length && !postTournamentPractice ? (
							<Select
								id="t-select"
								sx={{
									color: "white",
									".MuiOutlinedInput-notchedOutline": {
										borderColor: "rgba(228, 219, 233, 0.25)",
									},
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: "rgba(228, 219, 233, 0.25)",
									},
									"&:hover .MuiOutlinedInput-notchedOutline": {
										borderColor: "rgba(228, 219, 233, 0.25)",
									},
									".MuiSvgIcon-root ": {
										fill: "white !important",
									},
								}}
								value={wordRoundOption}
								label="Select Round"
								onChange={handleWordRoundChange}
								input={<BootstrapInput />}>
								{wordData.map((wd, i) => (
									<MenuItem key={i} value={wd.round}>
										Round {wd.round}
									</MenuItem>
								))}
							</Select>
						) : null}
					</div>
				</div>
				<div
					style={{
						width: "100%",
						marginBottom: 10,
					}}>
					{wordList &&
						wordList.round &&
						[GAME_FORMATS.JUMBLE, GAME_FORMATS.AUDIO].includes(roundFormat) ? (
						<TableContainer
							component={Paper}
							sx={{
								maxHeight: "60vh",
							}}>
							<Table
								stickyHeader
								sx={{ maxWidth: "100%" }}
								aria-label="simple table"
								size="small">
								<TableHead>
									<StyledTableRow style={{ padding: 8 }}>
										{wordList.jumbledString &&
											wordList.jumbledString.length &&
											wordList.jumbledString[0] !== null ? (
											<StyledTableCell align="center">
												Jumbled String
											</StyledTableCell>
										) : null}
										<StyledTableCell align="center">Answer</StyledTableCell>
										<StyledTableCell align="center">
											Your Response
										</StyledTableCell>
									</StyledTableRow>
								</TableHead>
								<TableBody>
									{wordList.attemptedWords.map((attWrd, i) => (
										<StyledTableRow
											key={`${i}`}
											sx={{
												"&:last-child td, &:last-child th": { border: 0 },
											}}>
											{wordList.jumbledString &&
												wordList.jumbledString.length &&
												wordList.jumbledString[0] !== null ? (
												<StyledTableCell align="center">
													{wordList.jumbledString[i]}
												</StyledTableCell>
											) : null}
											<StyledTableCell
												align="center"
												dangerouslySetInnerHTML={{
													__html: JSON.parse(attWrd).question,
												}}></StyledTableCell>
											<StyledTableCell
												align="center"
												style={{
													color: wordList.results
														? wordList.results[i]
															? "#ccf900"
															: "#ff5050"
														: "white",
												}}>
												{wordList.responses[i]}
											</StyledTableCell>
										</StyledTableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					) : null}
					{wordList &&
						wordList.round &&
						[
							GAME_FORMATS.QUIZ,
							GAME_FORMATS.IMAGE,
							GAME_FORMATS.AUDIOCLIP,
							GAME_FORMATS.IMAGE_JUMBLED,
							GAME_FORMATS.FLASH_IMAGES,
						].includes(roundFormat) ? (
						<div style={{ marginLeft: "10px", marginRight: "10px" }}>
							<CarouselComponent
								dataLength={wordList.attemptedWords.length}
								afterChange={pauseAudioClip}
								itemsPerPage={1}>
								{renderCards(roundFormat)}
							</CarouselComponent>
						</div>
					) : null}
					{wordList &&
						wordList.round &&
						[GAME_FORMATS.MEMORY_CARDS, GAME_FORMATS.CODING_ALGOS].includes(
							roundFormat
						) ? (
						<div
							className="flex flex-wrap justify-center tect-center items-center text-white h-[160px]"
							style={{
								marginLeft: "10px",
								marginRight: "10px",
								marginTop: "10px",
								marginBottom: "10px",
							}}>
							{roundFormat === GAME_FORMATS.CODING_ALGOS
								? "Make your algorithm round"
								: "Memory Cards Round"}
						</div>
					) : null}
					{wordList &&
						wordList.round &&
						[GAME_FORMATS.TANGRAM].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.attemptedWords.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{renderTangramCards()}
						</CarouselComponent>
					) : null}
					{wordList &&
						wordList.round &&
						[GAME_FORMATS.GEO_LOCATOR].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.attemptedWords.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{renderGeoLocatorCards()}
						</CarouselComponent>
					) : null}
					{wordList &&
						wordList.round &&
						[GAME_FORMATS.CATEGORIES].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.sentWords.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{renderCategryCards()}
						</CarouselComponent>
					) : null}
					{wordList &&
						wordList.round &&
						[GAME_FORMATS.MATCHING_COLUMNS].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.sentWords.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{rendermMatchingColumnCards()}
						</CarouselComponent>
					) : null}
					{wordList &&
						wordList.round &&
						[GAME_FORMATS.NUMBER_LINE].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.scoreBreakdown.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{renderNumberLineCards()}
						</CarouselComponent>
					) : null}
					{wordList &&
						wordList.round &&
						[GAME_FORMATS.HANGMAN].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.sentWords.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{renderHangManCards()}
						</CarouselComponent>
					) : null}
					{wordList &&
						wordList.round &&
						[GAME_FORMATS.SCRABBLE].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.responseWords.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{renderScrabbleCards()}
						</CarouselComponent>
					) : null}
					{wordList &&
						wordList.round &&
						[GAME_FORMATS.SHOPPING_CART].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.responsePurchases.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{renderShoppingCartCards()}
						</CarouselComponent>
					) : null}

					{wordList &&
						wordList.round &&
						[GAME_FORMATS.RACOON_DIENES].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.scoreBreakdown.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{renderRacoonDienesCards()}
						</CarouselComponent>
					) : null}

					{/*AUDIO_COMPREHENSION*/}

					{wordList &&
						wordList.round &&
						[GAME_FORMATS.AUDIO_COMPREHENSION].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.userResponses.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{renderAudioComprehensionCards()}
						</CarouselComponent>
					) : null}

					{wordList &&
						wordList.round &&
						[GAME_FORMATS.READING_COMPREHENSION].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.userResponses.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{renderReadingComprehensionCards()}
						</CarouselComponent>
					) : null}

					{wordList &&
						wordList.round &&
						[GAME_FORMATS.THISORTHAT].includes(roundFormat) ? (
						<CarouselComponent
							dataLength={wordList.attemptedWords.length}
							afterChange={pauseAudioClip}
							itemsPerPage={1}>
							{renderThisOrThatCards()}
						</CarouselComponent>
					) : null}

					{renderParagraphPopup()}



				</div>
			</div>
		</div>
	);
};
